import React, { useState, useEffect } from 'react';
import { incomeAPI } from '../api';

const Income = () => {
  const [incomes, setIncomes] = useState([]);
  const [incomeTypes, setIncomeTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchIncomes();
    fetchIncomeTypes();
  }, [selectedType, selectedStatus, currentPage]);

  const fetchIncomes = async () => {
    try {
      setLoading(true);
      const params = {
        type: selectedType || undefined,
        status: selectedStatus || undefined,
        page: currentPage,
        limit: 20,
      };

      const response = await incomeAPI.getMyIncome(params);
      setIncomes(response.data.incomes);
      setTotalPages(response.data.totalPages);
      setError('');
    } catch (err) {
      setError('Failed to load income data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchIncomeTypes = async () => {
    try {
      const response = await incomeAPI.getIncomeByTypes();
      setIncomeTypes(response.data);
    } catch (err) {
      console.error('Error fetching income types:', err);
    }
  };

  const formatIncomeType = (type) => {
    return type?.replace(/_/g, ' ').toLowerCase();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-50 text-yellow-800';
      case 'CREDITED':
        return 'bg-green-50 text-green-800';
      case 'PROCESSED':
        return 'bg-blue-50 text-blue-800';
      default:
        return 'bg-gray-50 text-gray-800';
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 py-10">
      <div className="mb-10">
        <h1 className="text-4xl font-bold text-secondary mb-2">Income & Earnings</h1>
        <p className="text-gray-500">View your income history and breakdown</p>
      </div>

      {/* Income Summary Cards */}
      {incomeTypes.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {incomeTypes.map((income, idx) => (
            <div key={idx} className="card bg-white rounded-lg shadow-md p-5">
              <div className="text-gray-600 text-xs font-semibold uppercase mb-2">{formatIncomeType(income._id)}</div>
              <div className="text-2xl font-bold text-primary mb-2">Rs. {income.total?.toLocaleString()}</div>
              <div className="text-xs text-gray-500">{income.count} transactions</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <select 
          value={selectedType} 
          onChange={(e) => {
            setSelectedType(e.target.value);
            setCurrentPage(1);
          }}
          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 bg-white"
        >
          <option value="">All Income Types</option>
          <option value="DIRECT_SPONSOR">Direct Sponsor Bonus</option>
          <option value="LEVEL_INCOME">Level Income</option>
          <option value="MONTHLY_PRODUCT_REWARD">Monthly Product Reward</option>
          <option value="DAILY_BOOSTING">Daily Boosting</option>
          <option value="REAL_TIME_BONANZA">Real Time Bonanza</option>
          <option value="BONANZA_CAR">Bonanza Car Payout</option>
          <option value="LIFETIME_REWARD">Lifetime Reward</option>
          <option value="ROYALTY_CLUB">Royalty Club</option>
        </select>

        <select 
          value={selectedStatus} 
          onChange={(e) => {
            setSelectedStatus(e.target.value);
            setCurrentPage(1);
          }}
          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 bg-white"
        >
          <option value="">All Status</option>
          <option value="PENDING">Pending</option>
          <option value="CREDITED">Credited</option>
          <option value="PROCESSED">Processed</option>
        </select>
      </div>

      {/* Error Message */}
      {error && <div className="alert alert-error mb-6">{error}</div>}

      {/* Income Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-500">Loading income data...</div>
      ) : incomes.length === 0 ? (
        <div className="flex items-center justify-center py-20 text-gray-500">No income transactions found</div>
      ) : (
        <>
          <div className="card bg-white rounded-lg shadow-md overflow-hidden mb-8">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Date</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Type</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Gross Amount</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Charges</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Net Amount</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {incomes.map((income) => (
                    <tr key={income._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {new Date(income.createdAt).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: '2-digit',
                          year: '2-digit',
                        })}
                      </td>
                      <td className="px-6 py-4 text-sm text-secondary font-medium">{formatIncomeType(income.type)}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-700">Rs. {income.amount?.toLocaleString()}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-700">
                        Rs. {((income.serviceCharge || 0) + (income.tdsDeduction || 0) + (income.adminCharge || 0))?.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-bold text-primary">Rs. {income.netAmount?.toLocaleString()}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`status-badge px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(income.status)}`}>
                          {income.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4">
              <button 
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-gray-700 font-semibold">{currentPage} of {totalPages}</span>
              <button 
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Income Information */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold text-secondary mb-6">Income Types Explained</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="card bg-white rounded-lg shadow-md p-6">
            <h4 className="font-bold text-secondary mb-2">Direct Sponsor Bonus</h4>
            <p className="text-sm text-gray-700">15% commission on every direct recruit's purchase</p>
          </div>
          <div className="card bg-white rounded-lg shadow-md p-6">
            <h4 className="font-bold text-secondary mb-2">Level Income</h4>
            <p className="text-sm text-gray-700">Commissions from your entire network (up to 15 levels). Requires minimum 11 direct sponsors.</p>
          </div>
          <div className="card bg-white rounded-lg shadow-md p-6">
            <h4 className="font-bold text-secondary mb-2">Monthly Product Reward</h4>
            <p className="text-sm text-gray-700">Rewards based on total monthly purchase volume</p>
          </div>
          <div className="card bg-white rounded-lg shadow-md p-6">
            <h4 className="font-bold text-secondary mb-2">Daily Boosting</h4>
            <p className="text-sm text-gray-700">Additional daily bonuses from your team</p>
          </div>
          <div className="card bg-white rounded-lg shadow-md p-6">
            <h4 className="font-bold text-secondary mb-2">Real Time Bonanza</h4>
            <p className="text-sm text-gray-700">Exponential rewards from team growth milestones</p>
          </div>
          <div className="card bg-white rounded-lg shadow-md p-6">
            <h4 className="font-bold text-secondary mb-2">Bonanza Car</h4>
            <p className="text-sm text-gray-700">THAR car payout paid as ₹1,00,000 monthly installments over 11 months</p>
          </div>
          <div className="card bg-white rounded-lg shadow-md p-6">
            <h4 className="font-bold text-secondary mb-2">Lifetime Reward</h4>
            <p className="text-sm text-gray-700">One-time reward for achieving higher ranks</p>
          </div>
          <div className="card bg-white rounded-lg shadow-md p-6">
            <h4 className="font-bold text-secondary mb-2">Royalty Club</h4>
            <p className="text-sm text-gray-700">Fortuner car payout delivered as ₹1,00,000 monthly installments over 51 months</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Income;
